import { FILTER_PRESETS, type FilterPreset } from '../data/filterCatalog';
import { NodeType, createInitialWorkflow, type StreamType, type WorkflowDocument, type WorkflowEdge, type WorkflowNode } from '../nodes/NodeTypes';
import { edgeStream, insertPreset, nextNodeId } from './workflowGraph';
import { streamEdgeStyle } from './streamStyles';

export interface CommandImportResult {
  workflow: WorkflowDocument;
  warnings: string[];
}

function tokenize(command: string): string[] {
  const tokens: string[] = [];
  let token = '';
  let quote = '';
  const characters = Array.from(command.trim());
  for (let index = 0; index < characters.length; index += 1) {
    const character = characters[index];
    if (character === '\\' && quote !== "'") {
      const next = characters[index + 1];
      const escapedInQuote = quote === '"' && ['"', '\\', '$', '`'].includes(next);
      const escapedOutsideQuote = !quote && (/[\s]/.test(next || '') || next === '"' || next === "'" || next === '\\');
      if (next !== undefined && (escapedInQuote || escapedOutsideQuote)) {
        token += next;
        index += 1;
      } else token += character;
      continue;
    }
    if (quote) {
      if (character === quote) quote = '';
      else token += character;
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      continue;
    }
    if (/\s/.test(character)) {
      if (token) tokens.push(token);
      token = '';
    } else token += character;
  }
  if (token) tokens.push(token);
  return tokens;
}

function splitUnescaped(value: string, delimiter: string): string[] {
  const parts: string[] = [];
  let part = '';
  let escaped = false;
  let quote = '';
  for (const character of value) {
    if (escaped) {
      part += '\\' + character;
      escaped = false;
      continue;
    }
    if (character === '\\') {
      escaped = true;
      continue;
    }
    if (quote) {
      if (character === quote) quote = '';
      part += character;
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      part += character;
    } else if (character === delimiter) {
      parts.push(part.trim());
      part = '';
    } else part += character;
  }
  if (escaped) part += '\\';
  if (part.trim()) parts.push(part.trim());
  return parts;
}

function getFilterName(expression: string): string {
  return expression.trim().match(/^([a-zA-Z0-9_]+)/)?.[1] || '';
}

function captureTemplateParams(expression: string, preset: FilterPreset): Record<string, string> | null {
  const template = preset.command;
  const params: Record<string, string> = {};
  const keys = Array.from(template.matchAll(/\{([a-zA-Z0-9_]+)\}/g)).map((match) => match[1]);
  if (!keys.length) return {};
  const segments = template.split(/\{[a-zA-Z0-9_]+\}/g);
  const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = segments.map((segment, index) => escapeRegex(segment) + (index < keys.length ? '(.+?)' : '')).join('');
  const matched = expression.match(new RegExp('^' + pattern + '$'));
  if (!matched) return null;
  keys.forEach((key, index) => { params[key] = matched[index + 1].replace(/\\(.)/g, '$1'); });
  return params;
}

function presetForExpression(expression: string, streamType: StreamType): { preset: FilterPreset; params: Record<string, string> } | null {
  const name = getFilterName(expression);
  const preset = FILTER_PRESETS.find((item) => item.operation === 'filter' && item.streamType === streamType && getFilterName(item.command) === name);
  if (!preset) return null;
  const params = captureTemplateParams(expression, preset);
  return params ? { preset, params } : null;
}

function splitFilterChain(value: string, streamType: StreamType): string[] {
  const parts = splitUnescaped(value, ',');
  const presets = FILTER_PRESETS.filter((preset) => preset.streamType === streamType && preset.command.includes(','));
  const result: string[] = [];
  for (let index = 0; index < parts.length;) {
    const composite = presets.find((preset) => {
      const count = preset.command.split(',').length;
      const candidate = parts.slice(index, index + count).join(',');
      return getFilterName(parts[index]) === getFilterName(preset.command) && !!captureTemplateParams(candidate, preset);
    });
    if (composite) {
      const count = composite.command.split(',').length;
      result.push(parts.slice(index, index + count).join(','));
      index += count;
    } else {
      result.push(parts[index]);
      index += 1;
    }
  }
  return result;
}

function customPreset(expression: string, streamType: StreamType): FilterPreset {
  const name = getFilterName(expression) || '自定义滤镜';
  return {
    id: 'custom-' + name,
    label: `自定义：${name}`,
    description: '从 FFmpeg 命令导入的滤镜表达式。',
    category: '自定义',
    streamType,
    operation: 'filter',
    command: expression,
    params: {},
    beginner: false,
  };
}

function connect(source: WorkflowNode, target: WorkflowNode, streamType: StreamType, id: string, targetHandle = 'input'): WorkflowEdge {
  return {
    id,
    source: source.id,
    target: target.id,
    sourceHandle: source.type === NodeType.INPUT ? streamType : 'output',
    targetHandle: target.type === NodeType.OUTPUT ? streamType : targetHandle,
    data: { streamType },
    type: 'smoothstep',
    style: streamEdgeStyle(streamType),
  };
}

function parseFilterChain(
  workflow: WorkflowDocument,
  expressions: string[],
  streamType: StreamType,
  warnings: string[],
): WorkflowDocument {
  return expressions.reduce((current, expression) => {
    const match = presetForExpression(expression, streamType);
    const preset = match?.preset || customPreset(expression, streamType);
    const inserted = insertPreset(current, preset, nextNodeId(current.nodes, 'filter'));
    const nodes = inserted.workflow.nodes.map((node) => node.id === inserted.nodeId && node.type === NodeType.PROCESSING
      ? { ...node, data: { ...node.data, params: match?.params || {}, customFilter: !match, command: match ? preset.command : expression } }
      : node);
    if (!match) warnings.push(`“${getFilterName(expression)}”未匹配内置滤镜，已作为自定义表达式导入。`);
    return { ...inserted.workflow, nodes };
  }, workflow);
}

export function parseFFmpegCommand(command: string): CommandImportResult {
  const tokens = tokenize(command);
  if (!tokens.length) throw new Error('请输入一条 FFmpeg 命令。');
  const warnings: string[] = [];
  let seek = '';
  let duration = '';
  const inputs: Array<{ filename: string; seek: string; duration: string }> = [];
  const videoFilters: string[] = [];
  const audioFilters: string[] = [];
  let complexGraph = '';
  const outputParams: Record<string, string> = {
    videoCodec: 'libx264', audioCodec: 'aac', quality: '23', audioBitrate: '128k', preset: 'medium',
  };
  let outputFilename = '';
  const knownValueOptions = new Set(['-ss', '-t', '-i', '-vf', '-filter:v', '-filter:a', '-af', '-filter_complex', '-c:v', '-c:a', '-crf', '-b:v', '-b:a', '-r', '-pix_fmt', '-preset', '-movflags']);
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token === 'ffmpeg' || token.endsWith('/ffmpeg') || token.endsWith('\\ffmpeg.exe')) continue;
    if (knownValueOptions.has(token)) {
      const value = tokens[index + 1];
      if (value === undefined) {
        warnings.push(`${token} 缺少参数，已忽略。`);
        continue;
      }
      index += 1;
      if (token === '-i') inputs.push({ filename: value, seek, duration }), seek = '', duration = '';
      else if (token === '-ss') seek = value;
      else if (token === '-t') duration = value;
      else if (token === '-vf' || token === '-filter:v') videoFilters.push(...splitFilterChain(value, 'video'));
      else if (token === '-af' || token === '-filter:a') audioFilters.push(...splitFilterChain(value, 'audio'));
      else if (token === '-filter_complex') complexGraph = value;
      else if (token === '-c:v') outputParams.videoCodec = value;
      else if (token === '-c:a') outputParams.audioCodec = value;
      else if (token === '-crf') outputParams.quality = value;
      else if (token === '-b:v') outputParams.videoBitrate = value;
      else if (token === '-b:a') outputParams.audioBitrate = value;
      else if (token === '-r') outputParams.frameRate = value;
      else if (token === '-pix_fmt') outputParams.pixelFormat = value;
      else if (token === '-preset') outputParams.preset = value;
      else if (token === '-movflags' && value.includes('faststart')) outputParams.fastStart = 'true';
      continue;
    }
    if (token === '-an') outputParams.disableAudio = 'true';
    else if (token === '-vn') outputParams.disableVideo = 'true';
    else if (token.startsWith('-')) warnings.push(`参数 ${token} 未映射到工作流设置。`);
    else if (inputs.length && index === tokens.length - 1) outputFilename = token;
  }
  if (!inputs.length) throw new Error('没有找到 -i 输入文件；请检查命令格式。');
  if (!outputFilename) outputFilename = 'output.mp4';

  const initial = createInitialWorkflow();
  const output = initial.nodes.find((node) => node.type === NodeType.OUTPUT) as WorkflowNode;
  const inputNodes = inputs.map((input, index) => ({
    id: index === 0 ? 'input-1' : `input-${index + 1}`,
    type: NodeType.INPUT,
    deletable: true,
    position: { x: 100, y: 170 + index * 150 },
    data: { label: index === 0 ? '输入文件' : `输入 ${index + 1}`, params: { filename: input.filename, seek: input.seek, duration: input.duration } },
  } as WorkflowNode));
  const outputNode: WorkflowNode = { ...output, data: { ...output.data, params: { ...outputParams, filename: outputFilename } } };
  let workflow: WorkflowDocument = {
    version: 3,
    nodes: [...inputNodes, outputNode],
    edges: inputNodes.flatMap((node) => [connect(node, outputNode, 'video', `e-${node.id}-video`), connect(node, outputNode, 'audio', `e-${node.id}-audio`)]),
  };

  if (complexGraph) {
    warnings.push('复合滤镜图已保留为高级自定义节点；请检查多路输入标签。');
    const statements = splitUnescaped(complexGraph, ';');
    const labelSources = new Map<string, { node: WorkflowNode; streamType: StreamType }>();
    inputNodes.forEach((node, index) => {
      labelSources.set(`${index}:v`, { node, streamType: 'video' });
      labelSources.set(`${index}:a`, { node, streamType: 'audio' });
    });
    const graphNodes: WorkflowNode[] = [];
    const graphEdges: WorkflowEdge[] = [];
    statements.forEach((statement, statementIndex) => {
      const prefix = statement.match(/^(?:\[[^\]]+\])+/)?.[0] || '';
      const suffix = statement.match(/(?:\[[^\]]+\])+\s*$/)?.[0] || '';
      if (!prefix || !suffix || prefix === suffix) {
        warnings.push('部分 -filter_complex 表达式的流标签无法识别，已跳过。');
        return;
      }
      const inputLabels = Array.from(prefix.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1]);
      const outputLabels = Array.from(suffix.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1]);
      const expression = statement.slice(prefix.length, statement.length - suffix.length).trim();
      const inputSources = inputLabels.map((label) => labelSources.get(label));
      const resolvedInputs = inputSources.filter((source): source is { node: WorkflowNode; streamType: StreamType } => !!source);
      if (resolvedInputs.length !== inputLabels.length || !expression) {
        warnings.push('部分 -filter_complex 输入标签没有对应节点，已跳过该表达式。');
        return;
      }
      const streamType = resolvedInputs[0].streamType;
      const nodeId = `custom-${statementIndex + 1}`;
      const customNode: WorkflowNode = {
        id: nodeId,
        type: NodeType.PROCESSING,
        position: { x: 350 + statementIndex * 240, y: streamType === 'audio' ? 350 : 200 },
        data: { label: `自定义：${getFilterName(expression) || '滤镜图'}`, category: '自定义', streamType, isAudio: streamType === 'audio', operation: 'filter', command: expression, params: {}, inputCount: resolvedInputs.length, customFilter: true },
      };
      graphNodes.push(customNode);
      resolvedInputs.forEach((source, inputIndex) => {
        graphEdges.push(connect(source.node, customNode, source.streamType, `e-${source.node.id}-${nodeId}-${inputIndex}`, inputIndex === 0 ? 'input' : 'secondary'));
      });
      outputLabels.forEach((label) => labelSources.set(label, { node: customNode, streamType }));
    });
    if (graphNodes.length) {
      workflow = { ...workflow, nodes: [...inputNodes, ...graphNodes, outputNode], edges: graphEdges };
      const candidates = graphNodes.filter((node) => !graphEdges.some((edge) => edge.source === node.id));
      (candidates.length ? candidates : [graphNodes[graphNodes.length - 1]]).forEach((node) => {
        const outgoingStream: StreamType = node.data.streamType || 'video';
        workflow.edges.push(connect(node, outputNode, outgoingStream, `e-${node.id}-output`));
      });
    }
  } else {
    workflow = parseFilterChain(workflow, videoFilters, 'video', warnings);
    workflow = parseFilterChain(workflow, audioFilters, 'audio', warnings);
  }

  return { workflow, warnings };
}
